import { Graphics, TextStyle, Text } from 'pixi.js';
import { FancyButton } from '@pixi/ui';
import { COLORS, FONTS, LAYOUT, ANIMATION } from '../constants/theme';

export interface UIButtonOptions {
  text: string;
  width?: number;
  height?: number;
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning';
  disabled?: boolean;
  onClick?: () => void;
}

/**
 * Reusable button component using @pixi/ui FancyButton
 * Drop-in replacement for PixiButton with same API
 */
export class UIButton extends FancyButton {
  private buttonOptions: Required<Omit<UIButtonOptions, 'onClick'>> & { onClick?: () => void };
  private onPressConnection: (() => void) | null = null;

  constructor(options: UIButtonOptions) {
    const opts: Required<Omit<UIButtonOptions, 'onClick'>> & { onClick?: () => void } = {
      width: 200,
      height: LAYOUT.BUTTON_HEIGHT,
      variant: 'primary',
      disabled: false,
      ...options,
    };

    const { width, height, variant, disabled, text } = opts;
    const color = UIButton.getVariantColor(variant);

    // Create views for each state
    const defaultView = UIButton.createButtonGraphics(width, height, color);
    const hoverView = UIButton.createButtonGraphics(width, height, UIButton.adjustColor(color, 16));
    const pressedView = UIButton.createButtonGraphics(width, height, UIButton.adjustColor(color, -32));
    const disabledView = UIButton.createButtonGraphics(width, height, COLORS.BG_SECONDARY);

    // Create text style
    const textStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.MD,
      fontWeight: 'bold',
      fill: COLORS.TEXT_PRIMARY,
    });

    super({
      defaultView,
      hoverView,
      pressedView,
      disabledView,
      text: new Text({ text, style: textStyle }),
      anchor: 0.5,
      animations: {
        hover: {
          props: { scale: { x: 1.02, y: 1.02 } },
          duration: ANIMATION.HOVER_DURATION * 1000,
        },
        pressed: {
          props: { scale: { x: 0.98, y: 0.98 } },
          duration: 50,
        },
      },
    });

    this.buttonOptions = opts;

    // Set up click handler
    if (opts.onClick) {
      this.onPressConnection = opts.onClick;
      this.onPress.connect(this.onPressConnection);
    }

    // Set initial disabled state
    if (disabled) {
      this.enabled = false;
    }

    // Accessibility
    this.accessible = true;
    this.accessibleType = 'button';
    this.accessibleTitle = text;
  }

  private static getVariantColor(variant: 'primary' | 'secondary' | 'danger' | 'success' | 'warning'): number {
    switch (variant) {
      case 'primary':
        return COLORS.PLAYER1;
      case 'secondary':
        return COLORS.BG_TERTIARY;
      case 'danger':
        return COLORS.DANGER;
      case 'success':
        return 0x22c55e; // Green
      case 'warning':
        return 0xeab308; // Yellow
      default:
        return COLORS.PLAYER1;
    }
  }

  private static adjustColor(color: number, amount: number): number {
    const r = Math.min(255, Math.max(0, ((color >> 16) & 0xff) + amount));
    const g = Math.min(255, Math.max(0, ((color >> 8) & 0xff) + amount));
    const b = Math.min(255, Math.max(0, (color & 0xff) + amount));
    return (r << 16) | (g << 8) | b;
  }

  private static createButtonGraphics(width: number, height: number, fillColor: number): Graphics {
    const g = new Graphics();

    // Draw from (0,0) - FancyButton's anchor handles centering
    g.roundRect(0, 0, width, height, LAYOUT.BORDER_RADIUS);
    g.fill({ color: fillColor });

    // Border
    g.roundRect(0, 0, width, height, LAYOUT.BORDER_RADIUS);
    g.stroke({ color: 0xffffff, width: 1, alpha: 0.1 });

    return g;
  }

  /**
   * Update button text
   */
  setText(newText: string): void {
    this.buttonOptions.text = newText;
    this.text = newText;
    this.accessibleTitle = newText;
  }

  /**
   * Update disabled state
   */
  setDisabled(disabled: boolean): void {
    this.buttonOptions.disabled = disabled;
    this.enabled = !disabled;
  }

  /**
   * Update button variant
   */
  setVariant(variant: 'primary' | 'secondary' | 'danger' | 'success' | 'warning'): void {
    if (this.buttonOptions.variant === variant) return;

    this.buttonOptions.variant = variant;
    const color = UIButton.getVariantColor(variant);
    const { width, height } = this.buttonOptions;

    // Update all views with new colors
    this.defaultView = UIButton.createButtonGraphics(width, height, color);
    this.hoverView = UIButton.createButtonGraphics(width, height, UIButton.adjustColor(color, 16));
    this.pressedView = UIButton.createButtonGraphics(width, height, UIButton.adjustColor(color, -32));
  }

  /**
   * Clean up signal connections
   */
  override destroy(): void {
    if (this.onPressConnection) {
      this.onPress.disconnect(this.onPressConnection);
      this.onPressConnection = null;
    }
    super.destroy();
  }
}
